import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, RequireAuth } from './lib/auth';
import { api, APIError } from './lib/api';
import { LoadingState } from './components/LoadingState';
import Calendar from './routes/Calendar';
import Dashboard from './routes/Dashboard';
import Today from './routes/Today';
import Training from './routes/Training';
import ReviewScreen from './routes/Review';
import Insurance from './routes/Insurance';
import Login from './routes/Login';
import Onboarding from './routes/Onboarding';
import Settings from './routes/Settings';
import Verify from './routes/Verify';

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/verify" element={<Verify />} />
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <Onboarding />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <RequireBet>
                <Dashboard />
              </RequireBet>
            </RequireAuth>
          }
        />
        <Route
          path="/today"
          element={
            <RequireAuth>
              <RequireBet>
                <Today />
              </RequireBet>
            </RequireAuth>
          }
        />
        <Route
          path="/calendar"
          element={
            <RequireAuth>
              <RequireBet>
                <Calendar />
              </RequireBet>
            </RequireAuth>
          }
        />
        <Route
          path="/training"
          element={
            <RequireAuth>
              <RequireBet>
                <Training />
              </RequireBet>
            </RequireAuth>
          }
        />
        <Route
          path="/review"
          element={
            <RequireAuth>
              <RequireBet>
                <ReviewScreen />
              </RequireBet>
            </RequireAuth>
          }
        />
        <Route
          path="/insurance"
          element={
            <RequireAuth>
              <RequireBet>
                <Insurance />
              </RequireBet>
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />
        <Route
          path="*"
          element={
            <div className="min-h-full flex items-center justify-center p-6 text-sm text-fg-muted">
              Not found.
            </div>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

// RequireBet redirects to /onboarding if the authenticated user hasn't
// committed a bet yet, or to /login if their session expired mid-flow. The
// check is one GET /bet:
//   - 200 → render children
//   - 404 → no bet → redirect to /onboarding
//   - 401 → session expired → redirect to /login
//   - any other error → render children optimistically (the inner screen
//     will surface a meaningful error rather than letting us blackhole it)
function RequireBet({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'checking' | 'has-bet' | 'no-bet' | 'unauth'>(
    'checking',
  );
  const location = useLocation();
  useEffect(() => {
    let cancelled = false;
    api
      .getBet()
      .then(() => {
        if (!cancelled) setState('has-bet');
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof APIError && e.status === 404) {
          setState('no-bet');
        } else if (e instanceof APIError && e.status === 401) {
          setState('unauth');
        } else {
          setState('has-bet');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);
  if (state === 'checking') {
    return (
      <div className="min-h-full flex items-center justify-center text-fg-muted">
        <LoadingState />
      </div>
    );
  }
  if (state === 'no-bet') {
    return <Navigate to="/onboarding" replace />;
  }
  if (state === 'unauth') {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}
